import React, { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  Form,
  TvInput,
  TvAlert,
  Spinner,
  TvButton,
  TvIconButton,
  TvDiv,
  TvLabel,
  TvSmall,
} from "@trustvid/design-system";
import { ResetPasswordAPI } from "../../../services/auth";
import { toast } from "react-toastify";
import { validatePassword } from "../../../utils/password-validation";
import AuthSplitLayout from "./AuthSplitLayout";
import { AUTH_FIELD_LABEL_CLASS, RESET_PASSWORD_PAGE_COPY } from "./loginPageContent";

const validationSchema = Yup.object({
  new_password: Yup.string()
    .nullable()
    .required("New Password is required")
    .test("is-valid-password", null, function validatePw(value) {
      const result = validatePassword(value);
      if (result === true) {
        return true;
      }
      const errors = result.split(", ");
      return this.createError({
        message: (
          <ul className="text-danger mb-0 ps-3">
            {errors.map((err, index) => (
              <li key={index}>{err}</li>
            ))}
          </ul>
        ),
      });
    }),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref("new_password"), null], "Passwords must match")
    .required("Confirm Password is required"),
});

const ResetPassword = () => {
  document.title = RESET_PASSWORD_PAGE_COPY.pageTitle;
  const { resetToken } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const formik = useFormik({
    initialValues: {
      new_password: "",
      confirmPassword: "",
    },
    validationSchema,
    onSubmit: async (values) => {
      setLoading(true);
      setError("");
      setSuccess("");

      try {
        const payload = {
          token: resetToken,
          new_password: values.new_password,
        };

        const response = await ResetPasswordAPI(payload);

        if (response?.error) {
          setError(response.error);
          navigate("/forgot-password");
        } else {
          setSuccess("Password has been reset successfully.");
          toast.success("Password has been reset successfully!");
          navigate("/login");
        }
      } catch (err) {
        setError("Something went wrong, please try again later.");
        navigate("/forgot-password");
      } finally {
        setLoading(false);
      }
    },
  });

  const hasPasswords =
    formik.values.new_password.length > 0 && formik.values.confirmPassword.length > 0;

  return (
    <AuthSplitLayout
      pageTestId="reset-password-page"
      cardTestId="reset-password-form-card"
      brandTestId="auth-platform-brand"
      showCardHeading={false}
      cardTitle={RESET_PASSWORD_PAGE_COPY.pageTitle}
      welcomeHeading={RESET_PASSWORD_PAGE_COPY.pageTitle}
      welcomeHeadingTestId="reset-password-card-title"
      cardSubtitle={RESET_PASSWORD_PAGE_COPY.cardSubtitle}
      featurePanelHeading={RESET_PASSWORD_PAGE_COPY.featurePanelHeading}
    >
      <Form onSubmit={formik.handleSubmit} data-testid="reset-password-form" className="tv-login-page__form">
        {error ? (
          <TvAlert color="danger" className="mb-3" data-testid="reset-password-error" fade={false}>
            {error}
          </TvAlert>
        ) : null}
        {success ? (
          <TvAlert color="success" className="mb-3" fade={false}>
            {success}
          </TvAlert>
        ) : null}

        <TvDiv className="mb-3">
          <TvLabel htmlFor="reset-new-password" className={AUTH_FIELD_LABEL_CLASS}>
            {RESET_PASSWORD_PAGE_COPY.newPasswordLabel}
          </TvLabel>
          <TvDiv className="position-relative">
            <TvInput
              id="reset-new-password"
              name="new_password"
              type={showNewPassword ? "text" : "password"}
              autoComplete="new-password"
              placeholder={RESET_PASSWORD_PAGE_COPY.newPasswordPlaceholder}
              aria-label={RESET_PASSWORD_PAGE_COPY.newPasswordLabel}
              className="tv-login-page__field tv-login-page__field--with-toggle"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.new_password}
              invalid={formik.touched.new_password && !!formik.errors.new_password}
            />
            <TvIconButton
              type="button"
              color="link"
              className="tv-login-page__password-toggle position-absolute top-50 end-0 translate-middle-y text-muted"
              onClick={() => setShowNewPassword((prev) => !prev)}
              aria-label={showNewPassword ? "Hide new password" : "Show new password"}
            >
              <i className={`mdi ${showNewPassword ? "mdi-eye-off" : "mdi-eye"} fs-5`} />
            </TvIconButton>
          </TvDiv>
          {formik.touched.new_password && formik.errors.new_password ? (
            <TvDiv className="text-danger mt-1 small">{formik.errors.new_password}</TvDiv>
          ) : null}
        </TvDiv>

        <TvDiv className="mb-4">
          <TvLabel htmlFor="reset-confirm-password" className={AUTH_FIELD_LABEL_CLASS}>
            {RESET_PASSWORD_PAGE_COPY.confirmPasswordLabel}
          </TvLabel>
          <TvDiv className="position-relative">
            <TvInput
              id="reset-confirm-password"
              name="confirmPassword"
              type={showConfirmPassword ? "text" : "password"}
              autoComplete="new-password"
              placeholder={RESET_PASSWORD_PAGE_COPY.confirmPasswordPlaceholder}
              aria-label={RESET_PASSWORD_PAGE_COPY.confirmPasswordLabel}
              className="tv-login-page__field tv-login-page__field--with-toggle"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.confirmPassword}
              invalid={formik.touched.confirmPassword && !!formik.errors.confirmPassword}
            />
            <TvIconButton
              type="button"
              color="link"
              className="tv-login-page__password-toggle position-absolute top-50 end-0 translate-middle-y text-muted"
              onClick={() => setShowConfirmPassword((prev) => !prev)}
              aria-label={showConfirmPassword ? "Hide confirm password" : "Show confirm password"}
            >
              <i className={`mdi ${showConfirmPassword ? "mdi-eye-off" : "mdi-eye"} fs-5`} />
            </TvIconButton>
          </TvDiv>
          {formik.touched.confirmPassword && formik.errors.confirmPassword ? (
            <TvSmall className="text-danger d-block mt-1">{formik.errors.confirmPassword}</TvSmall>
          ) : null}
        </TvDiv>

        <TvDiv className="d-grid mb-3">
          <TvButton
            color="primary"
            type="submit"
            disabled={loading}
            className={`tv-login-page__submit${!hasPasswords && !loading ? " disabled" : ""}`}
          >
            {loading ? <Spinner size="sm" color="light" /> : RESET_PASSWORD_PAGE_COPY.resetButton}
          </TvButton>
        </TvDiv>

        <TvDiv className="text-center text-lg-start">
          <Link to="/login" className="tv-login-page__text-link fw-semibold text-decoration-none">
            {RESET_PASSWORD_PAGE_COPY.backToSignIn}
          </Link>
        </TvDiv>
      </Form>
    </AuthSplitLayout>
  );
};

export default ResetPassword;
